import React, { useState, useEffect, useCallback } from 'react';
import { 
  Headphones, Clock, CheckCircle, Star, AlertCircle,
  Phone, MessageSquare, Calendar, MoreVertical, Eye,
  X, ChevronDown, TrendingUp, TrendingDown, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_URL = window.location.origin;

const CustomerService = () => {
  const userId = useUserId();
  
  // State
  const [stats, setStats] = useState({});
  const [priorityTickets, setPriorityTickets] = useState([]);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Category colors
  const categoryColors = {
    'Design Revision': 'bg-blue-500',
    'Technical': 'bg-orange-500',
    'Creative': 'bg-green-500',
    'Web Design': 'bg-purple-500',
    'Billing': 'bg-pink-500'
  };

  const priorityColors = {
    'High': 'bg-red-500',
    'Medium': 'bg-yellow-500',
    'Low': 'bg-gray-500'
  };

  const statusColors = {
    'Open': 'bg-gray-600 text-gray-200',
    'In Progress': 'bg-purple-600 text-white',
    'Resolved': 'bg-green-600 text-white',
    'Closed': 'bg-gray-700 text-gray-300'
  };

  // Fetch data
  const fetchStats = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/customer-service/stats?user_id=${userId}`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPriorityTickets = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/customer-service/priority-tickets?user_id=${userId}&limit=5`);
      const data = await res.json();
      setPriorityTickets(data);
    } catch (error) {
      console.error('Error fetching priority tickets:', error);
    }
  };

  const fetchResolvedTickets = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/customer-service/resolved-tickets?user_id=${userId}&limit=5`);
      const data = await res.json();
      setResolvedTickets(data);
    } catch (error) {
      console.error('Error fetching resolved tickets:', error);
    }
  };

  // Seed data
  const seedData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customer-service/seed?user_id=${userId}`, { method: 'POST' });
      const data = await res.json();
      if (data.seeded) {
        toast.success('Sample ticket data loaded');
        fetchStats();
        fetchPriorityTickets();
        fetchResolvedTickets();
      }
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchPriorityTickets(), fetchResolvedTickets()]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId]);

  // Auto-seed if no data
  useEffect(() => {
    if (!loading && priorityTickets.length === 0 && userId) {
      seedData();
    }
  }, [loading, priorityTickets.length, userId]);

  // Get time ago string
  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Get resolution time display
  const getResolutionTime = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}.${Math.floor(mins/6)} hours` : `${hours} hours`;
  };

  // View ticket details
  const viewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading customer service dashboard...</div>
      </div>
    );
  }

  // Categories for display
  const categories = stats.categories || {};
  const categoryList = [
    { name: 'Design Revision', count: categories['Design Revision'] || 15 },
    { name: 'Technical', count: categories['Technical'] || 10 },
    { name: 'Creative', count: categories['Creative'] || 8 },
    { name: 'Web Design', count: categories['Web Design'] || 6 },
    { name: 'Billing', count: categories['Billing'] || 3 }
  ];

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Customer Service</h1>
          <p className="text-gray-400 mt-1">Manage client support tickets and design inquiries</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Open Tickets */}
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">OPEN TICKETS</span>
              <Headphones size={20} className="text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.open_tickets || 42}</p>
            <p className={`text-sm ${(stats.open_tickets_change || -8) < 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.open_tickets_change || -8} vs yesterday
            </p>
          </div>

          {/* Avg Response Time */}
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">AVG RESPONSE TIME</span>
              <Clock size={20} className="text-cyan-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.avg_response_time || 1.2}h</p>
            <p className={`text-sm ${(stats.response_time_improvement || -0.4) < 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.response_time_improvement || -0.4}h improvement
            </p>
          </div>

          {/* Resolved Today */}
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">RESOLVED TODAY</span>
              <CheckCircle size={20} className="text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.resolved_today || 68}</p>
            <p className="text-sm text-green-400">+{stats.resolved_today_change || 15} vs yesterday</p>
          </div>

          {/* Satisfaction Score */}
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">SATISFACTION SCORE</span>
              <TrendingUp size={20} className="text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.satisfaction_score || 4.8}/5</p>
            <p className="text-sm text-green-400">+{stats.satisfaction_change || 0.3} this week</p>
          </div>
        </div>

        {/* Tickets by Category */}
        <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-6 mb-6">
          <h2 className="text-white font-semibold mb-4">Tickets by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {categoryList.map(cat => (
              <div 
                key={cat.name}
                className={`${categoryColors[cat.name]} rounded-xl p-6 text-center`}
              >
                <p className="text-4xl font-bold text-white mb-2">{cat.count}</p>
                <p className="text-white/80 text-sm">{cat.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Priority Tickets */}
          <div className="lg:col-span-2 bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-6">
            <h2 className="text-white font-semibold mb-4">Priority Tickets</h2>
            <div className="space-y-4">
              {priorityTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <AlertCircle size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No priority tickets</p>
                </div>
              ) : (
                priorityTickets.map(ticket => (
                  <div key={ticket.id} className="bg-black rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium">
                          {ticket.client_name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-gray-400 text-xs">{ticket.ticket_id}</span>
                            <span className={`px-2 py-0.5 rounded text-xs text-white ${priorityColors[ticket.priority]}`}>
                              {ticket.priority}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${statusColors[ticket.status]}`}>
                              {ticket.status}
                            </span>
                          </div>
                          <h3 className="text-white font-medium">{ticket.title}</h3>
                          <p className="text-gray-500 text-sm">
                            {ticket.client_name} • {ticket.category} • {getTimeAgo(ticket.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-xs">Assigned to</p>
                        <p className="text-white font-medium">{ticket.assigned_to}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewTicket(ticket)}
                        className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        View Details
                      </button>
                      <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
                        <MessageSquare size={18} />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
                        <Phone size={18} />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
                        <Calendar size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recently Resolved */}
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-6">
            <h2 className="text-white font-semibold mb-4">Recently Resolved</h2>
            <div className="space-y-4">
              {resolvedTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <CheckCircle size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No resolved tickets</p>
                </div>
              ) : (
                resolvedTickets.map(ticket => (
                  <div key={ticket.id} className="bg-black rounded-xl p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-gray-500 text-xs">{ticket.ticket_id}</span>
                      <CheckCircle size={18} className="text-green-400" />
                    </div>
                    <h3 className="text-white font-medium mb-2">{ticket.title}</h3>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-400">Client: <span className="text-gray-300">{ticket.client_name}</span></p>
                      <p className="text-gray-400">Resolved by: <span className="text-gray-300">{ticket.assigned_to}</span></p>
                      <p className="text-gray-400">Time: <span className="text-gray-300">{getResolutionTime(ticket.resolution_time_minutes)}</span></p>
                    </div>
                    {/* Star Rating */}
                    {ticket.satisfaction_rating && (
                      <div className="flex items-center gap-0.5 mt-3">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star 
                            key={star} 
                            size={16} 
                            className={star <= ticket.satisfaction_rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Ticket Detail Modal */}
        {showTicketModal && selectedTicket && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div>
                  <span className="text-gray-400 text-sm">{selectedTicket.ticket_id}</span>
                  <h2 className="text-xl font-bold text-white">{selectedTicket.title}</h2>
                </div>
                <button onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs text-white ${priorityColors[selectedTicket.priority]}`}>
                    {selectedTicket.priority}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[selectedTicket.status]}`}>
                    {selectedTicket.status}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs text-white ${categoryColors[selectedTicket.category]}`}>
                    {selectedTicket.category}
                  </span>
                </div>
                
                <p className="text-gray-300">{selectedTicket.description}</p>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
                  <div>
                    <p className="text-gray-500 text-sm">Client</p>
                    <p className="text-white">{selectedTicket.client_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Assigned To</p>
                    <p className="text-white">{selectedTicket.assigned_to}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Created</p>
                    <p className="text-white">{getTimeAgo(selectedTicket.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Response Time</p>
                    <p className="text-white">{selectedTicket.response_time_hours}h</p>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => toast.info('Resolve ticket feature coming soon')}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                  >
                    Mark Resolved
                  </button>
                  <button
                    onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }}
                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerService;
